<template>
  <div class="ml-56 h-screen">
    <!-- Page content -->
    <div class="h-full flex flex-col bg-truegray-100">
      <!-- Page header -->
      <PageHeader
        :overline="headerConfig.overline"
        :title="headerConfig.title"
      />
      <!-- /Page header -->

      <!-- Stats content -->
      <div
        class="flex flex-col flex-grow items-start overflow-y-scroll p-6 w-full"
      >
        <!-- Loading -->
        <div
          v-if="isLoading"
          class="h-52 flex flex-col justify-center items-center w-full mt-16"
        >
          <lottie :options="loadingAnimationOptions" />
          <span class="text-center">Loading your statistics</span>
        </div>
        <!-- /Loading -->

        <!-- Stats tabs -->
        <template v-else>
          <!-- Tab buttons -->
          <div
            :style="{ height: 'max-content' }"
            class="flex w-full flex-shrink-0 border-b-2 border-white"
          >
            <div
              v-for="(tab, i) in tabViews"
              :key="tab.id"
              :class="[
                'flex cursor-pointer items-center justify-center bg-white text-black h-16 w-16 transform transition-all',
                i > 0 ? 'rounded-tr-xl' : 'rounded-tl-xl',
                {
                  'text-cw-alt': isActiveTab(i),
                  'opacity-50': !isActiveTab(i),
                  'hover:text-cw-alt hover:opacity-100': !isDisabledTab(i),
                  'opacity-25 cursor-not-allowed': isDisabledTab(i),
                },
              ]"
              :title="tab.title"
              @click="selectTab(i)"
            >
              <span
                :class="!isActiveTab(i) ? 'opacity-50' : 'scale-125'"
                class="transition-all transform"
                v-html="tab.svg"
              />
            </div>
          </div>
          <!-- /Tab buttons -->

          <!-- Active tab content -->
          <div
            class="flex flex-wrap lg:flex-nowrap gap-5 pt-5 px-6 lg:px-2 w-full"
          >
            <!-- Posts tab content -->
            <template v-if="activeTab.name === 'TabPosts'">
              <!-- LHS: Stats cards -->
              <div
                class="w-full xl:w-1/2 grid grid-cols-1 lg:grid-cols-2 gap-4"
              >
                <!-- Post Counter -->
                <StatisticsCardCounter
                  type="posts"
                  :title="postCounterTitle"
                  getter="contributors/getPostCounter"
                  modal-name="cw-contributor-post-pack"
                />
                <!-- /Post Counter -->

                <!-- Total School Shares -->
                <StatisticsCard
                  title="Total School Shares"
                  content-classes="pt-8"
                >
                  <template #content>
                    <div class="flex w-full items-center justify-around">
                      <div
                        class="flex flex-col items-center justify-center h-[120px] w-[120px] p-4 rounded-full cw-gradient text-white"
                      >
                        <img
                          :style="{ width: '35px' }"
                          class="text-white invert"
                          src="/img/icons/contributor_dashboard/share-white.svg"
                          alt="Shares icon"
                        />
                        <h3 class="font-montserrat font-bold">
                          {{ formatNumber(organisationActivityPostShares) }}
                        </h3>
                      </div>
                    </div>
                    <InputDropdownSelector
                      :type="'POST_SHARES'"
                      :wrapper-classes="'mt-6'"
                    />
                  </template>
                </StatisticsCard>
                <!-- /Total School Shares -->

                <!-- Total Post Reach -->
                <StatisticsCard title="Total Post Reach" content-classes="pt-8">
                  <template #content>
                    <div class="flex w-full items-center justify-center gap-6">
                      <div class="flex p-4 rounded-full cw-gradient text-white">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          class="w-8 h-8"
                        >
                          <path
                            d="M10 8a3 3 0 100-6 3 3 0 000 6zM3.465 14.493a1.23 1.23 0 00.41 1.412A9.957 9.957 0 0010 18c2.31 0 4.438-.784 6.131-2.1.43-.333.604-.903.408-1.41a7.002 7.002 0 00-13.074.003z"
                          />
                        </svg>
                      </div>
                      <h3 class="font-montserrat m-0 text-cw-900">
                        {{ formatNumber(organisationActivityPostReach) }}
                      </h3>
                    </div>
                    <InputDropdownSelector
                      :type="'POST_REACH'"
                      :wrapper-classes="'mt-6'"
                    />
                  </template>
                </StatisticsCard>
                <!-- /Total Post Reach -->

                <!-- Post Categories -->
                <StatisticsCardPostCategories context="ORGANISATION" />
                <!-- /Post Categories -->
              </div>
              <!-- /LHS: Stats cards -->

              <!-- RHS: Your Posts -->
              <div class="w-full xl:w-1/2 rounded-xl bg-white py-4 px-8">
                <div
                  class="flex items-center justify-between w-full border-b border-truegray-300 mb-8 py-3"
                >
                  <h5>Your Posts</h5>
                  <InputDropdownListSelector
                    :selected-option="postsSortingSelectedOption"
                    :options="CD_POSTS_SORT_BY_OPTIONS"
                    @on-submit="updatePostsSorting"
                  />
                </div>

                <template v-if="posts.length">
                  <!-- Posts data -->
                  <div
                    v-for="(item, i) in posts"
                    :key="i"
                    :class="postsLoading ? 'opacity-30' : 'opacity-100'"
                    class="grid grid-cols-12 items-center gap-x-2 mb-4 sm:mb-2 transition-all"
                  >
                    <div class="col-span-8 sm:col-span-4">
                      <p
                        :title="item.title"
                        class="m-0 text-truegray-500 pr-6 truncate overflow-ellipsis"
                      >
                        {{ item.title }}
                      </p>
                    </div>
                    <div class="col-span-2 sm:col-span-2">
                      <div
                        title="School shares"
                        class="flex w-1/5 text-cw-900 space-x-2"
                      >
                        <img
                          class="w-4"
                          src="/img/icons/contributor_dashboard/share-dark.svg"
                          alt="Shares icon"
                        />
                        <p class="font-light space-x-2 mb-0">
                          {{ formatNumber(item.statistics.shares) }}
                        </p>
                      </div>
                    </div>
                    <div class="col-span-2 sm:col-span-2">
                      <div
                        title="Post reach"
                        class="flex space-x-2 text-cw-900"
                      >
                        <img
                          src="/img/icons/contributor_dashboard/member-dark.svg"
                          alt="Audience icon"
                        />
                        <p class="font-light m-0">
                          {{ formatNumber(item.statistics.audience) }}
                        </p>
                      </div>
                    </div>
                    <div class="col-span-2 sm:col-span-3">
                      <div
                        title="Post creation date"
                        class="flex space-x-2 text-cw-900"
                      >
                        <img
                          src="/img/icons/contributor_dashboard/calendar-dark.svg"
                          alt="Calendar icon"
                        />

                        <p class="text-xs font-light m-0">
                          {{ item.created_at_ui }}
                        </p>
                      </div>
                    </div>
                    <div class="col-span-2 sm:col-span-1">
                      <button
                        class="btn px-2 py-1 mb-0 ml-auto text-cw-700 hover:text-cw-alt hover:rotate-360 transition-all duration-300"
                        title="More post stats"
                        @click="showPostStatsModal(item)"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          class="w-4 h-4"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M15.28 9.47a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 1 1-1.06-1.06L13.69 10 9.97 6.28a.75.75 0 0 1 1.06-1.06l4.25 4.25ZM6.03 5.22l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L8.69 10 4.97 6.28a.75.75 0 0 1 1.06-1.06Z"
                            clip-rule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <!-- /Posts data -->

                  <!-- Pagination -->
                  <div
                    v-if="canShowPagination"
                    class="flex justify-between items-center mt-4 h-10"
                  >
                    <span
                      :disabled="isFirstPage || postsLoading"
                      :class="[
                        isFirstPage || postsLoading
                          ? 'opacity-25 cursor-not-allowed'
                          : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
                      ]"
                      class="text-gray-500"
                      @click="getPosts({ page: pagination.current_page - 1 })"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M11 17l-5-5m0 0l5-5m-5 5h12"
                        />
                      </svg>
                    </span>
                    <span v-if="!postsLoading" class="text-center text-xs">
                      Page {{ pagination.current_page }} of
                      {{ pagination.last_page }}
                    </span>
                    <span
                      v-else
                      class="w-8 h-8 flex items-center justify-center"
                    >
                      <lottie :options="loadingAnimationOptions" />
                    </span>
                    <span
                      :disabled="isLastPage || postsLoading"
                      :class="[
                        isLastPage || postsLoading
                          ? 'opacity-25 cursor-not-allowed'
                          : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
                      ]"
                      class="text-gray-500"
                      @click="getPosts({ page: pagination.current_page + 1 })"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M13 7l5 5m0 0l-5 5m5-5H6"
                        />
                      </svg>
                    </span>
                  </div>
                  <!-- /Pagination -->
                </template>
                <div v-else class="flex justify-center pb-2">
                  <span class="text-sm">No posts</span>
                </div>
              </div>
              <!-- /RHS: Your Posts -->
            </template>
            <!-- /Posts tab content -->

            <!-- Connections tab content -->
            <template v-else>
              <!-- LHS: Connections stats cards -->
              <div
                class="w-full xl:w-1/2 grid grid-cols-1 lg:grid-cols-2 gap-4"
              >
                <!-- Connections Counter -->
                <StatisticsCardCounter
                  type="connections"
                  title="Connections Counter"
                  getter="contributors/getConnectionsCounter"
                >
                  <template #no-content> No connections! </template>
                </StatisticsCardCounter>
                <!-- /Connections Counter -->

                <!-- Total Views -->
                <StatisticsCard title="Total Views" content-classes="pt-4">
                  <template #content>
                    <div class="flex w-full items-center justify-around">
                      <div
                        class="flex flex-col items-center justify-center gap-2 h-[120px] w-[120px] p-4 rounded-full cw-gradient text-white"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          class="w-7 h-7"
                        >
                          <path
                            d="M10 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"
                          />
                          <path
                            fill-rule="evenodd"
                            d="M.664 10.59a1.651 1.651 0 0 1 0-1.186A10.004 10.004 0 0 1 10 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0 1 10 17c-4.257 0-7.893-2.66-9.336-6.41ZM14 10a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z"
                            clip-rule="evenodd"
                          />
                        </svg>

                        <h3 class="font-montserrat font-bold mb-0">
                          {{ formatNumber(connectionsImpressionsCount) }}
                        </h3>
                      </div>
                    </div>
                    <!--
                      <InputDropdownSelector
                      :type="'POST_SHARES'"
                      :wrapper-classes="'mt-6'"
                      /> 
                    -->
                  </template>
                </StatisticsCard>
                <!-- /Total Views -->

                <!-- Total Leads -->
                <StatisticsCard title="Total Leads" content-classes="pt-4">
                  <template #content>
                    <div class="flex w-full items-center justify-center gap-6">
                      <div class="flex p-6 rounded-full cw-gradient text-white">
                        <img
                          src="/img/icons/contributor_dashboard/views-white.svg"
                          alt="Leads icon"
                          class="w-8 h-8"
                        />
                      </div>
                      <h3 class="font-montserrat m-0 text-cw-900">
                        {{ formatNumber(connectionsViewsCount) }}
                      </h3>
                    </div>
                    <!--
                      <InputDropdownSelector
                      :type="'POST_REACH'"
                      :wrapper-classes="'mt-6'"
                      /> 
                    -->
                  </template>
                </StatisticsCard>
                <!-- /Total Leads -->

                <!-- Total Contacted -->
                <StatisticsCard title="Total Contacted" content-classes="pt-4">
                  <template #content>
                    <div class="flex w-full items-center justify-center gap-6">
                      <div class="flex p-6 rounded-full cw-gradient text-white">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          class="w-8 h-8"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M10 2c-2.236 0-4.43.18-6.57.524C1.993 2.755 1 4.014 1 5.426v5.148c0 1.413.993 2.67 2.43 2.902.848.137 1.705.248 2.57.331v3.443a.75.75 0 0 0 1.28.53l3.58-3.579a.78.78 0 0 1 .527-.224 41.202 41.202 0 0 0 5.183-.5c1.437-.232 2.43-1.49 2.43-2.903V5.426c0-1.413-.993-2.67-2.43-2.902A41.289 41.289 0 0 0 10 2Zm0 7a1 1 0 1 0 0-2 1 1 0 0 0 0 2ZM8 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm5 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
                            clip-rule="evenodd"
                          />
                        </svg>
                      </div>
                      <h3 class="font-montserrat m-0 text-cw-900">
                        {{ formatNumber(connectionsContactedCount) }}
                      </h3>
                    </div>
                  </template>
                </StatisticsCard>
                <!-- /Total Contacted -->
              </div>
              <!-- /LHS: Connections stats cards -->

              <!-- RHS: Your Connections -->
              <StatisticsCardConnections />
              <!-- /RHS: Your Connections -->
            </template>
            <!-- /Connections tab content -->
          </div>
          <!-- /Stats content -->
        </template>
      </div>
      <!-- /Stats tabs -->
    </div>
    <!-- /Page content -->
  </div>
</template>

<script>
import { EventBus } from '../../../js/plugins/event-bus.js'
import { defineComponent, computed, ref, watch, onMounted } from 'vue'
import { useStore } from '../../../js/store/index.js'
import { formatNumber } from '../../../js/util/helper.js'
import PageHeader from '../components/PageHeader.vue'
import InputDropdownSelector from '../components/InputDropdownSelector.vue'
import InputDropdownListSelector from '../components/InputDropdownListSelector.vue'
import StatisticsCard from '../components/StatisticsCard.vue'
import StatisticsCardPostCategories from '../components/StatisticsCardPostCategories.vue'
import StatisticsCardCounter from '../components/StatisticsCardCounter.vue'
import StatisticsCardConnections from '../components/StatisticsCardConnections.vue'
import * as loadingAnimation from '../../../shared/components/loading-dark.json'
import { CD_POSTS_SORT_BY_OPTIONS } from '../../../js/util/constants.js'

export default defineComponent({
  name: 'Statistics',

  components: {
    PageHeader,
    StatisticsCard,
    StatisticsCardPostCategories,
    StatisticsCardCounter,
    StatisticsCardConnections,
    InputDropdownSelector,
    InputDropdownListSelector,
  },

  setup() {
    const store = useStore()

    const headerConfig = {
      overline: 'statistics',
      title: 'Your Organisation Reports',
    }

    // Loading
    const loadingAnimationOptions = { animationData: loadingAnimation.default }
    const isLoading = ref(false)
    const globalLoading = computed(() => store.getters['shared/isLoading'])
    watch(globalLoading, (val) => {
      isLoading.value = val
    })

    // Organisation
    const organisationId = computed(
      () => store.getters['contributors/getOrganisationId'],
    )
    watch(organisationId, () => init())

    // Tabs
    const tabViews = computed(() => [
      {
        name: 'TabPosts',
        title: 'Posts Statistics',
        context: 'posts',
        svg: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
              </svg>`,
        disabled: false,
      },
      {
        name: 'TabConnections',
        title: 'Connection Statistics',
        context: 'connections',
        svg: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
            </svg>`,
        disabled: false,
      },
    ])
    const activeTab = ref(tabViews.value[0])
    const isActiveTab = (i) => {
      return activeTab.value === tabViews.value[i]
    }
    const isDisabledTab = (i) => {
      return tabViews.value[i].disabled
    }
    const selectTab = (i) => {
      const newTab = tabViews.value[i]
      if (activeTab.value !== newTab) {
        if (!isDisabledTab(i)) {
          activeTab.value = newTab
        }
      }
    }

    // Posts list stats
    const posts = computed(() => {
      return store.getters['contributors/getPostsStats']
    })

    const postsLoading = ref(false)
    const pagination = ref(null)

    const canShowPagination = computed(() => {
      return pagination.value?.last_page > 1
    })

    const isFirstPage = computed(() => {
      return pagination?.value?.current_page === 1
    })

    const isLastPage = computed(() => {
      return pagination?.value?.current_page === pagination?.value?.last_page
    })

    const postsSortingSelectedOption = ref(CD_POSTS_SORT_BY_OPTIONS[0])

    const updatePostsSorting = (opt) => {
      postsSortingSelectedOption.value = opt
    }

    watch(postsSortingSelectedOption, () => getPosts({ page: 1 }))

    const getPosts = async ({
      page = pagination.value?.current_page,
      sortBy = postsSortingSelectedOption.value?.sortBy,
      orderBy = postsSortingSelectedOption.value?.orderBy,
    } = {}) => {
      postsLoading.value = true
      const payload = {
        page,
        sortBy,
        orderBy,
      }
      pagination.value = await store.dispatch(
        'contributors/dispatchUpdateStatsPosts',
        payload,
      )
      postsLoading.value = false
    }

    const showPostStatsModal = (item) => {
      EventBus.$emit('showModal', {
        action: 'cw-contributor-post-stats',
        data: {
          post: item,
        },
      })
    }

    // Cards - posts

    const postCounter = computed(() => {
      return store.getters['contributors/getPostCounter']
    })

    // This is duplicated in statistics.vue @todo - move to store?
    const postCounterTitle = computed(() => {
      return postCounter.value?.subscription
        ? 'Monthly Post Count'
        : 'Post Count'
    })

    const organisationActivityPostShares = computed(() => {
      return store.getters['contributors/getOrganisationActivityPostShares']
    })

    const organisationActivityPostReach = computed(() => {
      return store.getters['contributors/getOrganisationActivityPostReach']
    })

    const organisationActivityPostsMailedCount = computed(() => {
      return store.getters[
        'contributors/getOrganisationActivityPostsMailedCount'
      ]
    })

    const organisationActivityNewsletterReach = computed(() => {
      return store.getters[
        'contributors/getOrganisationActivityNewsletterReach'
      ]
    })

    // Cards - connections
    const connectionsImpressionsCount = computed(
      () => store.getters['contributors/getConnectionsImpressionsCount'],
    )

    const connectionsViewsCount = computed(
      () => store.getters['contributors/getConnectionsViewsCount'],
    )

    const connectionsContactedCount = computed(
      () => store.getters['contributors/getConnectionsContactedCount'],
    )

    const init = async () => {
      isLoading.value = true
      pagination.value = await store.dispatch(
        'contributors/dispatchInitPageStats',
      )
      isLoading.value = false
    }

    onMounted(init)

    return {
      formatNumber,
      loadingAnimationOptions,
      headerConfig,
      isLoading,

      // Tabs
      tabViews,
      activeTab,
      isActiveTab,
      isDisabledTab,
      selectTab,

      // Posts
      postsLoading,
      posts,
      pagination,
      canShowPagination,
      isFirstPage,
      isLastPage,
      getPosts,
      CD_POSTS_SORT_BY_OPTIONS,
      postsSortingSelectedOption,
      updatePostsSorting,
      showPostStatsModal,
      postCounterTitle,

      // Cards
      organisationActivityPostShares,
      organisationActivityPostReach,
      organisationActivityPostsMailedCount,
      organisationActivityNewsletterReach,
      connectionsImpressionsCount,
      connectionsViewsCount,
      connectionsContactedCount,
    }
  },
})
</script>
